import {
  Box,
  Flex,
  Text,
  Badge,
  VStack,
  HStack,
  Heading,
  Button,
  Skeleton,
  SkeletonText,
} from '@chakra-ui/react';
import { T } from '@transifex/react';
import React, { useState } from 'react';
import AutoEnrollContentHubUsersModal, {
  AutoEnrollMode,
} from './AutoEnrollContentHubUsersModal';
import { withErrorBoundary } from 'features/common/utils/errorBoundaries';
import { MembersErrorPage } from 'features/main/components/CustomErrorPages/MembersErrorPage';
import { useGetCurrentOrgSettings } from 'features/home/services/identity/organizationSettings';
import { useGetTenantsByOrgAndProductCode } from 'features/common/services/inventory/tenants';
import { fetchLastSuccessfulLoginOrganizationId } from 'features/common/auth/auth0Storage';
import EmptyState from 'features/common/components/EmptyState';

interface ConfigurationItemProps {
  title: string;
  description: string;
  isEnabled: boolean;
  isLoading?: boolean;
  onEnableClick: () => void;
  onDisableClick: () => void;
  enableButtonText?: string;
  disableButtonText?: string;
}

interface ConfigurationItemConfig {
  id: string;
  title: string;
  description: string;
  enableButtonText: string;
  disableButtonText: string;
  isEnabled: (data: any) => boolean;
  isLoading: boolean;
  onEnableClick: () => void;
  onDisableClick: () => void;
  modalComponent: React.ReactNode;
}

const ConfigurationItem = ({
  title,
  description,
  isEnabled,
  isLoading = false,
  onEnableClick,
  onDisableClick,
  enableButtonText = 'Enable',
  disableButtonText = 'Disable',
}: ConfigurationItemProps) => {
  return (
    <Box>
      <HStack spacing={10} alignItems='center' flexWrap='wrap'>
        <VStack align='stretch' spacing={4} width='600px'>
          <HStack spacing={3} align='center'>
            <Skeleton isLoaded={!isLoading}>
              <Heading as='h3' size='md'>
                <T _str={title} />
              </Heading>
            </Skeleton>
            <Skeleton isLoaded={!isLoading}>
              <Badge
                colorScheme={isEnabled ? 'green' : 'gray'}
                variant='subtle'
                fontSize='xs'
                fontWeight='bold'
                px={2}
                py={1}
                borderRadius='md'
              >
                <T _str={isEnabled ? 'ENABLED' : 'NOT ENABLED'} />
              </Badge>
            </Skeleton>
          </HStack>

          <SkeletonText isLoaded={!isLoading} noOfLines={2} spacing={2}>
            <Text>
              <T _str={description} />
            </Text>
          </SkeletonText>
        </VStack>

        <Skeleton isLoaded={!isLoading}>
          {isEnabled ? (
            <Button
              colorScheme='red'
              variant='solid'
              onClick={onDisableClick}
              size='md'
              flexShrink='0'
              minWidth='160px'
            >
              <T _str={disableButtonText} />
            </Button>
          ) : (
            <Button
              colorScheme='green'
              variant='solid'
              onClick={onEnableClick}
              size='md'
              flexShrink='0'
              minWidth='160px'
            >
              <T _str={enableButtonText} />
            </Button>
          )}
        </Skeleton>
      </HStack>
    </Box>
  );
};

export const Configuration = () => {
  const [autoEnrollModalOpen, setAutoEnrollModalOpen] = useState(false);
  const [autoEnrollModalMode, setAutoEnrollModalMode] =
    useState<AutoEnrollMode>('enable');

  const organizationId = fetchLastSuccessfulLoginOrganizationId() ?? '';
  const { isLoading: isCHTenantsLoading, data: chTenantsData } =
    useGetTenantsByOrgAndProductCode({
      organizationId,
      productCode: 'ContentHub|ContentDAM|ContentOperations',
    });

  const isCHConfigurationEnabled = (chTenantsData?.totalCount ?? 0) > 0;

  const { isLoading: isGetCurrentOrgSettingsLoading, data } =
    useGetCurrentOrgSettings({
      enabled: isCHConfigurationEnabled,
    });

  const isCHConfigLoading =
    isCHTenantsLoading || isGetCurrentOrgSettingsLoading;

  const handleAutoEnrollEnableClick = () => {
    setAutoEnrollModalMode('enable');
    setAutoEnrollModalOpen(true);
  };

  const handleAutoEnrollDisableClick = () => {
    setAutoEnrollModalMode('disable');
    setAutoEnrollModalOpen(true);
  };

  const handleAutoEnrollCloseModal = () => {
    setAutoEnrollModalOpen(false);
  };

  const allConfigurationItems: ConfigurationItemConfig[] = [
    {
      id: 'auto-enroll-ch-users',
      title: 'Automatic enrollment of Content Hub users',
      description:
        'While this setting is enabled, new and existing Content Hub users are automatically enrolled in the organization when they interact with associated AI functionality in Content Hub.',
      enableButtonText: 'Enable auto-enrollment',
      disableButtonText: 'Disable auto-enrollment',
      isEnabled: (data) => data?.data?.allowAutoEnrollmentOfChUsers ?? false,
      isLoading: isCHConfigLoading,
      onEnableClick: handleAutoEnrollEnableClick,
      onDisableClick: handleAutoEnrollDisableClick,
      modalComponent: (
        <AutoEnrollContentHubUsersModal
          isOpen={autoEnrollModalOpen}
          onClose={handleAutoEnrollCloseModal}
          mode={autoEnrollModalMode}
        />
      ),
    },
    // Add new config items here. Declare new data fetching hook and modal state above.
  ];

  const configurationItems = allConfigurationItems.filter((item) => {
    if (item.id === 'auto-enroll-ch-users') {
      return isCHConfigLoading || isCHConfigurationEnabled;
    }
    // Add more conditions for future config items
    return true;
  });

  // No configuration items to render
  if (configurationItems.length === 0) {
    return (
      <Flex flex={1} justifyContent='center' alignItems='center'>
        <EmptyState
          text='No configurations available'
          description="Your licensed products don't allow configuration options."
          dataTestId='no-configuration-available'
        />
      </Flex>
    );
  }

  return (
    <VStack align='stretch' spacing={6}>
      {configurationItems.map((item) => (
        <ConfigurationItem
          key={item.id}
          title={item.title}
          description={item.description}
          isEnabled={item.isEnabled(data)}
          isLoading={item.isLoading}
          onEnableClick={item.onEnableClick}
          onDisableClick={item.onDisableClick}
          enableButtonText={item.enableButtonText}
          disableButtonText={item.disableButtonText}
        />
      ))}

      {configurationItems.map((item) => (
        <React.Fragment key={`modal-${item.id}`}>
          {item.modalComponent}
        </React.Fragment>
      ))}
    </VStack>
  );
};

export const ConfigurationWithErrorHandling = withErrorBoundary(
  Configuration,
  MembersErrorPage,
);
